<template>
    <div class="min-h-screen">
        <div class="mx-auto py-8">
            <!-- Header -->
            <div class="mb-8">
                <div class="flex flex-col lg:flex-row lg:items-center justify-between mb-4">
                    <div>
                        <h1 class="text-3xl font-bold text-foxy-text">Сповіщення</h1>
                        <p class="foxy-muted mt-1">Відстежуйте всі оновлення та активність</p>
                    </div>
                    
                    <div class="flex items-center space-x-3 mt-2 lg:mt-0 ">
                        <button v-if="unreadCount > 0" @click="markAllAsRead" class="px-4 py-2 bg-foxy-action hover:bg-foxy-action/90 text-white rounded-lg font-medium transition-colors text-sm">
                            <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Позначити прочитаними
                        </button>
                        <button
                            @click="deleteAllRead"
                            :disabled="deleteReadPending"
                            :aria-busy="deleteReadPending ? 'true' : 'false'"
                            class="px-4 py-2 bg-foxy-elevated hover:bg-foxy-error/10 text-foxy-error rounded-lg font-medium transition-colors text-sm disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {{ deleteReadPending ? 'Зачекайте…' : 'Видалити прочитані' }}
                        </button>
                    </div>
                </div>
                
                <!-- Filters -->
                <notification-filters 
                    :current-filter="currentFilter"
                    :total-count="totalCount"
                    :unread-count="unreadCount"
                    @filter-change="onFilterChange"
                />
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex justify-center items-center py-20">
                <svg class="animate-spin h-12 w-12 text-foxy-action" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>

            <!-- Notifications List -->
            <div v-else-if="notifications.length > 0" class="space-y-3">
                <notification-card
                    v-for="notification in notifications"
                    :key="notification.id"
                    :notification="notification"
                    @click="handleNotificationClick"
                    @delete="deleteNotification"
                />
            </div>

            <!-- Empty State -->
            <div v-else class="foxy-panel !rounded-2xl shadow-sm p-12 text-center">
                <div class="mb-6">
                    <svg class="w-24 h-24 mx-auto text-foxy-text-secondary/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                </div>
                <h3 class="text-xl font-semibold text-foxy-text mb-2">Немає сповіщень</h3>
                <p class="foxy-muted">Ваші сповіщення з'являться тут</p>
            </div>

            <!-- Pagination -->
            <notification-pagination
                v-if="lastPage > 1"
                :current-page="currentPage"
                :last-page="lastPage"
                @page-change="loadPage"
            />
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import NotificationFilters from './NotificationFilters.vue';
import NotificationCard from './NotificationCard.vue';
import NotificationPagination from './NotificationPagination.vue';

export default {
    name: 'NotificationsPage',
    components: {
        NotificationFilters,
        NotificationCard,
        NotificationPagination
    },
    data() {
        return {
            notifications: [],
            unreadCount: 0,
            totalCount: 0,
            loading: false,
            currentFilter: 'all',
            currentPage: 1,
            lastPage: 1,
            deleteReadPending: false,
            notificationsRequest: null,
            notificationsRequestKey: null,
            notificationsRequestSequence: 0
        };
    },
    mounted() {
        this.fetchNotifications();
        window.addEventListener('focus', this.handleWindowFocus);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    beforeDestroy() {
        window.removeEventListener('focus', this.handleWindowFocus);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    },
    methods: {
        fetchNotifications(page = 1, { force = false } = {}) {
            const filter = this.currentFilter;
            const requestKey = `${filter}:${page}`;
            if (!force && this.notificationsRequest && this.notificationsRequestKey === requestKey) {
                return this.notificationsRequest;
            }

            const requestSequence = ++this.notificationsRequestSequence;
            this.loading = true;
            const params = new URLSearchParams({ page, filter });
            const request = axios.get(`/notifications/api?${params}`)
                .then((response) => {
                    if (requestSequence !== this.notificationsRequestSequence) return false;

                    this.notifications = response.data.notifications;
                    this.unreadCount = response.data.unread_count;
                    this.totalCount = response.data.total;
                    this.currentPage = response.data.current_page;
                    this.lastPage = response.data.last_page;
                    return true;
                })
                .catch((error) => {
                    if (requestSequence !== this.notificationsRequestSequence) return false;

                    console.error('Error fetching notifications:', error);
                    this.showNotification('Помилка завантаження сповіщень', 'error');
                    return false;
                })
                .finally(() => {
                    if (this.notificationsRequest === request) {
                        this.loading = false;
                        this.notificationsRequest = null;
                        this.notificationsRequestKey = null;
                    }
                });

            this.notificationsRequest = request;
            this.notificationsRequestKey = requestKey;

            return request;
        },

        handleWindowFocus() {
            if (!document.hidden) {
                this.fetchNotifications(this.currentPage);
            }
        },

        handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                this.fetchNotifications(this.currentPage);
            }
        },
        
        onFilterChange(filter) {
            this.currentFilter = filter;
            this.currentPage = 1;
            this.fetchNotifications();
        },
        
        loadPage(page) {
            if (page >= 1 && page <= this.lastPage) {
                this.fetchNotifications(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        
        async handleNotificationClick(notification) {
            // Mark as read
            if (!notification.is_read) {
                await this.markAsRead(notification.id);
            }
            
            // Navigate to the notification target
            const url = this.getNotificationUrl(notification);
            if (url) {
                window.location.href = url;
            }
        },
        
        getNotificationUrl(notification) {
            if (notification.target_url) {
                return notification.target_url;
            }

            const reviewCommentTypes = ['review_reply', 'review_comment_reply', 'review_comment_mention', 'review_comment_like'];
            if (reviewCommentTypes.includes(notification.type)) {
                return notification.target_url || '/notifications';
            }

            if (['review_like', 'review_like_milestone'].includes(notification.type) && notification.data) {
                // Для лайков рецензий переходим на страницу рецензии
                const bookIdentifier = notification.data.book_slug || notification.data.book_id;
                const reviewId = notification.data.review_id || notification.data.likeable_id;
                if (bookIdentifier && reviewId) {
                    return `/books/${bookIdentifier}/reviews/${reviewId}`;
                }
            } else if (['discussion_reply', 'discussion_comment_reply'].includes(notification.type) && notification.data) {
                // Используем discussion_slug (новые уведомления) или fallback на discussion_id (старые уведомления)
                const discussionIdentifier = notification.data.discussion_slug || notification.data.discussion_id;
                const replyId = notification.data.reply_id || notification.data.parent_reply_id;
                if (discussionIdentifier && replyId) {
                    return `/discussions/${discussionIdentifier}#reply-${replyId}`;
                } else if (discussionIdentifier) {
                    return `/discussions/${discussionIdentifier}`;
                }
            } else if (['discussion_like', 'discussion_like_milestone', 'discussion_comment_like', 'discussion_mention', 'discussion_reply_mention'].includes(notification.type) && notification.data) {
                // Используем discussion_slug для лайков и упоминаний
                const discussionIdentifier = notification.data.discussion_slug || notification.data.discussion_id;
                const replyId = notification.data.reply_id;
                if (discussionIdentifier && replyId) {
                    return `/discussions/${discussionIdentifier}#reply-${replyId}`;
                } else if (discussionIdentifier) {
                    return `/discussions/${discussionIdentifier}`;
                }
            }
            return null;
        },
        
        async markAsRead(id) {
            try {
                await axios.post(`/notifications/${id}/read`);
                
                const notification = this.notifications.find(n => n.id === id);
                if (notification) {
                    notification.is_read = true;
                }
                this.unreadCount = Math.max(0, this.unreadCount - 1);
            } catch (error) {
                console.error('Error marking notification as read:', error);
            }
        },
        
        async markAllAsRead() {
            try {
                await axios.post('/notifications/mark-all-read');
                
                this.notifications.forEach(n => n.is_read = true);
                this.unreadCount = 0;
                this.showNotification('Всі сповіщення позначено як прочитані', 'success');
            } catch (error) {
                console.error('Error marking all as read:', error);
                this.showNotification('Помилка при позначенні', 'error');
            }
        },
        
        async deleteNotification(id) {
            try {
                await axios.delete(`/notifications/${id}`);
                
                const index = this.notifications.findIndex(n => n.id === id);
                if (index !== -1) {
                    if (!this.notifications[index].is_read) {
                        this.unreadCount = Math.max(0, this.unreadCount - 1);
                    }
                    this.notifications.splice(index, 1);
                }
                this.showNotification('Сповіщення видалено', 'success');
            } catch (error) {
                console.error('Error deleting notification:', error);
                this.showNotification('Помилка видалення', 'error');
            }
        },
        
        async deleteAllRead() {
            if (this.deleteReadPending) return false;

            this.deleteReadPending = true;
            try {
                const confirmed = await confirm('Ви впевнені, що хочете видалити всі прочитані сповіщення?', 'Підтвердження', 'warning');
                if (!confirmed) return false;

                await axios.delete('/notifications/read/all');

                const refreshed = await this.fetchNotifications(1, { force: true });
                if (!refreshed) return false;

                this.showNotification('Прочитані сповіщення видалено', 'success');
                return true;
            } catch (error) {
                console.error('Error deleting read notifications:', error);
                this.showNotification('Помилка видалення', 'error');
                return false;
            } finally {
                this.deleteReadPending = false;
            }
        },
        
        showNotification(message, type = 'success') {
            this.$emit('notification', { message, type });
        }
    }
};
</script>
